import React from "react";
import { CheckCircle, Loader2, Search } from "lucide-react";
import type {
  ControlResponse,
  ImplementationStatus,
  ImplementedRequirementDetailResponse,
} from "../../../../../api/frameworksApi";
import { Dropdown } from "../../../../components/Dropdown";
import {
  FormTextarea,
  Modal,
} from "../../../../components/Modal";
import { IMPLEMENTATION_STATUSES } from "../../constants";

import type { PostImportIREntry } from "../../types";
export type { PostImportIREntry };

export interface PostImportControlStatusDialogProps {
  open: boolean;
  onClose: () => void;
  closePostImportPopups: () => void;
  postImportComponentTitle: string;
  postImportIRs: PostImportIREntry[];
  postImportLoading: boolean;
  postImportSearch: string;
  setPostImportSearch: React.Dispatch<React.SetStateAction<string>>;
  postImportSelectedIdx: number | null;
  setPostImportSelectedIdx: React.Dispatch<React.SetStateAction<number | null>>;
  handleSelectPostImportControl: (index: number) => void;
  postImportDescriptions: Record<string, string>;
  setPostImportDescriptions: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  postImportStatuses: Record<string, ImplementationStatus>;
  setPostImportStatuses: React.Dispatch<
    React.SetStateAction<Record<string, ImplementationStatus>>
  >;
  postImportStmtEdits: Record<
    string,
    { statementId: string; description: string; sortOrder: number }[]
  >;
  postImportControlDetails: Record<string, ControlResponse>;
  postImportLoadingControlId: string | null;
  postImportParamEdits: Record<string, { paramId: string; values: string }[]>;
  setPostImportParamEdits: React.Dispatch<
    React.SetStateAction<
      Record<string, { paramId: string; values: string }[]>
    >
  >;
  excludedIrIds: Set<string>;
  setExcludedIrIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  savedIrIds: Set<string>;
  savingIrId: string | null;
  bulkSaving: boolean;
  handleBulkSaveAll: () => void;
  handleSaveSingleIR: (
    ciId: string,
    ir: ImplementedRequirementDetailResponse,
  ) => void;
}

export function PostImportControlStatusDialog({
  open,
  onClose,
  closePostImportPopups,
  postImportComponentTitle,
  postImportIRs,
  postImportLoading,
  postImportSearch,
  setPostImportSearch,
  postImportSelectedIdx,
  setPostImportSelectedIdx,
  handleSelectPostImportControl,
  postImportDescriptions,
  setPostImportDescriptions,
  postImportStatuses,
  setPostImportStatuses,
  postImportStmtEdits,
  postImportControlDetails,
  postImportLoadingControlId,
  postImportParamEdits,
  setPostImportParamEdits,
  excludedIrIds,
  setExcludedIrIds,
  savedIrIds,
  savingIrId,
  bulkSaving,
  handleBulkSaveAll,
  handleSaveSingleIR,
}: PostImportControlStatusDialogProps) {
  const handleClose = () => {
    closePostImportPopups();
    setPostImportSelectedIdx(null);
    setPostImportSearch("");
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Controls - ${postImportComponentTitle}`}
      size="lg"
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-muted-foreground">
            {postImportIRs.length - excludedIrIds.size} of{" "}
            {postImportIRs.length} controls selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            {savedIrIds.size === postImportIRs.length - excludedIrIds.size &&
              postImportIRs.length - excludedIrIds.size > 0 ? (
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
              >
                Done
              </button>
            ) : (
              <button
                onClick={handleBulkSaveAll}
                disabled={
                  bulkSaving ||
                  postImportIRs.length - excludedIrIds.size === 0
                }
                className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {bulkSaving ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin inline mr-1.5" />
                    Saving
                  </>
                ) : (
                  `Save All (${postImportIRs.length - excludedIrIds.size})`
                )}
              </button>
            )}
          </div>
        </div>
      }
    >
      {postImportLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-6 animate-spin text-indigo-600 dark:text-indigo-400" />
          <span className="ml-2 text-sm text-muted-foreground">
            Loading control details
          </span>
        </div>
      ) : (
        <div className="flex gap-3 min-h-[400px]">
          {/* Left: controls list */}
          <div className="w-56 flex-shrink-0 rounded-lg border border-border overflow-hidden flex flex-col">
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground/50 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search controls"
                  value={postImportSearch}
                  onChange={(e) => setPostImportSearch(e.target.value)}
                  className="w-full pl-8 pr-2 py-1.5 text-xs bg-muted/50 rounded-md border-0 outline-none placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-indigo-500/30"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {postImportIRs
                .filter(
                  ({ ir, componentTitle }) =>
                    !postImportSearch ||
                    ir.controlId
                      .toLowerCase()
                      .includes(postImportSearch.toLowerCase()) ||
                    componentTitle
                      .toLowerCase()
                      .includes(postImportSearch.toLowerCase()) ||
                    (postImportDescriptions[ir.id] || ir.description || "")
                      .toLowerCase()
                      .includes(postImportSearch.toLowerCase()),
                )
                .map(({ ir, componentTitle }) => {
                  const realIdx = postImportIRs.findIndex(
                    (item) => item.ir.id === ir.id,
                  );
                  const isActive = postImportSelectedIdx === realIdx;
                  const isExcluded = excludedIrIds.has(ir.id);
                  return (
                    <button
                      key={ir.id}
                      type="button"
                      onClick={() => handleSelectPostImportControl(realIdx)}
                      className={`w-full text-left px-3 py-2.5 border-b border-border transition-colors ${isExcluded ? "opacity-50" : ""} ${isActive ? "bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-muted/50"}`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!isExcluded}
                          onChange={(e) => {
                            e.stopPropagation();
                            setExcludedIrIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(ir.id)) {
                                next.delete(ir.id);
                              } else {
                                next.add(ir.id);
                              }
                              return next;
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="size-3 rounded border-border accent-indigo-600 flex-shrink-0 cursor-pointer"
                        />
                        {savedIrIds.has(ir.id) ? (
                          <CheckCircle className="size-3 text-emerald-500 flex-shrink-0" />
                        ) : null}
                        <span
                          className={`text-[10px] font-black ${isExcluded ? "line-through text-muted-foreground" : isActive ? "text-indigo-600 dark:text-indigo-400" : "text-muted-foreground"}`}
                        >
                          {ir.controlId?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">
                        {postImportDescriptions[ir.id] ||
                          ir.description ||
                          ir.controlId?.toUpperCase()}
                      </p>
                      <p className="text-[10px] text-indigo-600 dark:text-indigo-400 mt-0.5 line-clamp-1">
                        {componentTitle}
                      </p>
                    </button>
                  );
                })}
            </div>
            <div className="px-3 py-2 border-t border-border bg-muted/30">
              <span className="text-[10px] text-muted-foreground">
                {postImportIRs.length - excludedIrIds.size} of{" "}
                {postImportIRs.length} selected · {savedIrIds.size} saved
              </span>
            </div>
          </div>

          {/* Right: control detail */}
          <div className="flex-1 rounded-lg border border-border overflow-y-auto">
            {postImportSelectedIdx !== null &&
              postImportIRs[postImportSelectedIdx] ? (
              (() => {
                const { ciId, ir, componentTitle } =
                  postImportIRs[postImportSelectedIdx];
                const detail = postImportControlDetails[ir.controlId];
                const isLoadingDetail =
                  postImportLoadingControlId === ir.controlId;
                const currentStatements = postImportStmtEdits[ir.id] || [];
                const currentParams = postImportParamEdits[ir.id] || [];
                return (
                  <div className="p-4 space-y-4">
                    {/* Header */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                          {ir.controlId?.toUpperCase()}
                        </span>
                        <h4 className="text-sm font-bold text-foreground">
                          {detail?.title ||
                            postImportDescriptions[ir.id] ||
                            ir.description ||
                            ir.controlId?.toUpperCase()}
                        </h4>
                      </div>
                      <p className="text-xs text-indigo-600 dark:text-indigo-400">
                        Component: {componentTitle}
                      </p>
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <label className="text-[10px] font-bold text-muted-foreground mb-1 block">
                            Status
                          </label>
                          <Dropdown
                            size="sm"
                            variant="form"
                            value={postImportStatuses[ir.id] || "planned"}
                            onChange={(v) =>
                              setPostImportStatuses((prev) => ({
                                ...prev,
                                [ir.id]: v as ImplementationStatus,
                              }))
                            }
                            options={IMPLEMENTATION_STATUSES.map((s) => ({
                              value: s.value,
                              label: s.label,
                            }))}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-border p-3 space-y-2">
                      <label className="text-xs font-bold text-foreground block">
                        Description
                      </label>
                      <FormTextarea
                        placeholder="Describe how this component satisfies the control"
                        value={postImportDescriptions[ir.id] || ""}
                        onChange={(value) =>
                          setPostImportDescriptions((prev) => ({
                            ...prev,
                            [ir.id]: value,
                          }))
                        }
                        rows={3}
                      />
                    </div>

                    {isLoadingDetail && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                        <Loader2 className="size-4 animate-spin" />
                        Loading control details
                      </div>
                    )}

                    {(detail ||
                      currentStatements.length > 0 ||
                      currentParams.length > 0) && (
                        <>
                          {/* Statements */}
                          {currentStatements.length > 0 && (
                            <div className="rounded-lg border border-border p-3 space-y-2">
                              <p className="text-xs font-bold text-foreground">
                                Statements
                              </p>
                              {currentStatements.map((stmt) => (
                                <div
                                  key={stmt.statementId}
                                  className="flex items-start gap-2.5 p-2.5 rounded-lg border border-indigo-300 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/5"
                                >
                                  <CheckCircle className="size-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-[10px] font-bold text-muted-foreground ">
                                      {stmt.statementId?.toUpperCase()}
                                    </span>
                                    {stmt.description && (
                                      <p className="text-xs text-foreground mt-0.5">
                                        {stmt.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Guidance */}
                          {detail &&
                            detail.parts.filter((p) => p.name === "guidance")
                              .length > 0 && (
                              <div className="rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5 p-3 space-y-1.5">
                                <p className="text-xs font-bold text-amber-800 dark:text-amber-400">
                                  Guidance
                                </p>
                                {detail.parts
                                  .filter((p) => p.name === "guidance")
                                  .map((part) => (
                                    <p
                                      key={part.id}
                                      className="text-xs text-amber-700 dark:text-amber-300/80"
                                    >
                                      {part.prose}
                                    </p>
                                  ))}
                              </div>
                            )}
                          {/* Parameters */}
                          {currentParams.length > 0 && (
                            <div className="rounded-lg border border-border p-3 space-y-3">
                              <p className="text-xs font-bold text-foreground">
                                Parameters
                              </p>
                              {currentParams.map((param) => {
                                const catalogParam = detail?.params?.find(
                                  (p) => p.paramId === param.paramId,
                                );
                                return (
                                  <div
                                    key={param.paramId}
                                    className="rounded-lg border border-dashed border-border p-3 space-y-1"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] font-bold text-muted-foreground ">
                                        {param.paramId}
                                      </span>
                                      {catalogParam?.label && (
                                        <span className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400">
                                          {catalogParam.label}
                                        </span>
                                      )}
                                    </div>
                                    <input
                                      type="text"
                                      placeholder={
                                        catalogParam?.values?.length
                                          ? `e.g. ${catalogParam.values.join(", ")}`
                                          : "Enter value"
                                      }
                                      value={param.values}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setPostImportParamEdits((prev) => {
                                          const params = [
                                            ...(prev[ir.id] || []),
                                          ];
                                          const idx = params.findIndex(
                                            (p) => p.paramId === param.paramId,
                                          );
                                          if (idx >= 0)
                                            params[idx] = {
                                              ...params[idx],
                                              values: val,
                                            };
                                          return { ...prev, [ir.id]: params };
                                        });
                                      }}
                                      className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-card text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-500"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    {!isLoadingDetail &&
                      !detail &&
                      currentStatements.length === 0 &&
                      currentParams.length === 0 && (
                        <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
                          Select this control to load statements, guidance,
                          and parameters when catalog details are available.
                        </div>
                      )}
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleSaveSingleIR(ciId, ir)}
                        disabled={
                          savingIrId === ir.id || excludedIrIds.has(ir.id)
                        }
                        className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                      >
                        {savingIrId === ir.id ? "Saving" : "Save Control"}
                      </button>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center py-16">
                <Search className="size-8 text-muted-foreground/30 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">
                  Select a control
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click a control from the list to view details
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
